import { createContext, useContext, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { useWebSocket } from "../hooks/useWebSocket";
import { LeadActivityMessageDelta, WebSocketMessage } from "../utils/websocket";
import { trpc, RouterOutputs } from "../trpc";
import MerchantContext from "./MerchantContext";
import {
  applyDeltasToInboxList,
  clearAllRecentDeltas,
  clearRecentDeltaEntry,
  recordRecentDelta,
} from "./inboxDeltaStore";

type WebSocketContextType = {
  isConnected: boolean;
  clearRecentDelta: (leadId: string) => void;
};

type InboxList = RouterOutputs["inbox"]["list"];

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

const FLUSH_DELAY_MS = 500;

export const WebSocketProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const { merchantId } = useContext(MerchantContext);

  const pendingLeadIds = useRef<Set<string>>(new Set());
  const pendingDeltas = useRef<Map<string, LeadActivityMessageDelta>>(new Map());
  const flushTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const merchantIdRef = useRef(merchantId);
  merchantIdRef.current = merchantId;

  const flush = useCallback(() => {
    const leadIds = Array.from(pendingLeadIds.current);
    const deltas = new Map(pendingDeltas.current);
    pendingLeadIds.current.clear();
    pendingDeltas.current.clear();
    flushTimeout.current = null;
    const currentMerchantId = merchantIdRef.current;
    if (leadIds.length === 0 || !currentMerchantId) return;

    if (deltas.size > 0) {
      for (const [leadId, delta] of deltas) {
        recordRecentDelta(leadId, delta);
      }
      const inboxKey = getQueryKey(trpc.inbox.list);
      queryClient.setQueriesData<InboxList>({ queryKey: inboxKey }, (old) =>
        applyDeltasToInboxList(old)
      );
    }

    utils.leads.list.invalidate();
    utils.leads.listActiveByPosition.invalidate();
    utils.inbox.list.invalidate(undefined, {
      predicate: (query) => {
        const input = (query.queryKey[1] as { input?: { page?: number; search?: string } } | undefined)
          ?.input;
        return input?.page === 0 && !input?.search;
      },
    });
    for (const leadId of leadIds) {
      utils.conversations.listConversationTimeline.invalidate({
        merchantId: currentMerchantId,
        leadId,
      });
      // Keep an open drawer/chat fresh: 24h window + AI state (conversations.get)
      // and profile fields (leads.get). Per-lead keys — no-ops unless mounted.
      utils.conversations.get.invalidate({ merchantId: currentMerchantId, leadId });
      utils.leads.get.invalidate({ merchantId: currentMerchantId, id: leadId });
    }
  }, [utils, queryClient]);

  const clearRecentDelta = useCallback((leadId: string) => {
    clearRecentDeltaEntry(leadId);
  }, []);

  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      pendingLeadIds.current.add(message.data.leadId);
      if (message.data.message) {
        pendingDeltas.current.set(message.data.leadId, message.data.message);
      }
      if (!flushTimeout.current) {
        flushTimeout.current = setTimeout(flush, FLUSH_DELAY_MS);
      }
    },
    [flush]
  );

  useEffect(() => {
    return () => {
      if (flushTimeout.current) {
        clearTimeout(flushTimeout.current);
        flushTimeout.current = null;
      }
      clearAllRecentDeltas();
    };
  }, []);

  const { isConnected } = useWebSocket({
    autoConnect: true,
    onMessage: handleMessage,
  });

  return (
    <WebSocketContext.Provider value={{ isConnected, clearRecentDelta }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
};
